import { FC } from 'react';
import { ActionIcon, Badge, Group, Text, ThemeIcon } from '@mantine/core';
import {
  IconArrowBackUp,
  IconBuildingStore,
  IconCheck,
  IconClipboardText,
  IconExternalLink,
  IconHammer,
  IconTruckDelivery,
  IconUserDollar,
} from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';

import { RouterOutput } from '../../../services/trpc';
import { formatDateTime } from '../../../utils/format';

type Notification = RouterOutput['notifications']['list'][number];

const notificationTypeConfig: Record<
  string,
  { icon: FC<{ size?: number }>; label: string; color: string }
> = {
  QUOTE_SIGNED: {
    icon: IconClipboardText,
    label: 'Marché',
    color: 'blue',
  },
  TECHNICAL_PLAN_SIGNED: {
    icon: IconClipboardText,
    label: 'Plan technique',
    color: 'violet',
  },
  DELIVERY_NOT_PAID: {
    icon: IconTruckDelivery,
    label: 'Livraison',
    color: 'orange',
  },
  INSTALLATION_PENDING: {
    icon: IconHammer,
    label: 'Installation',
    color: 'red',
  },
};

type Props = {
  notification: Notification;
  onMarkAsSeen?: (id: string) => void;
  onMarkAsActive?: (id: string) => void;
};

export const NotificationItem: FC<Props> = ({
  notification,
  onMarkAsSeen,
  onMarkAsActive,
}) => {
  const projectLink = `/app/projets/${notification.project.slug}`;
  const isActive = notification.status === 'ACTIVE';
  const isSeen = notification.status === 'SEEN';
  const config = notificationTypeConfig[notification.type];
  const TypeIcon = config?.icon ?? IconClipboardText;

  const msg = notification.message;
  const hasMessage = msg && msg !== '' && msg !== '-';
  const message = hasMessage ? msg : 'Un nouvel événement est survenu !';

  return (
    <Group justify="space-between" wrap="nowrap" py="xs" px="sm">
      <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
        <ThemeIcon variant="light" color={config?.color ?? 'gray'} size="sm">
          <TypeIcon size={14} />
        </ThemeIcon>
        <div style={{ flex: 1 }}>
          <Group gap={4}>
            <Text size="xs" fw={600} c={config?.color ?? 'gray'}>
              [{config?.label ?? notification.type}]
            </Text>
            <Text size="sm">{message}</Text>
          </Group>
          <Group gap={4} mt={4}>
            <Text size="xs" c="dimmed">
              Créé le {formatDateTime(notification.createdAt)}
            </Text>
            <Text size="xs" c="dimmed">
              ·
            </Text>
            <Text size="xs" c="dimmed">
              MAJ le {formatDateTime(notification.updatedAt)}
            </Text>
            <Text size="xs" c="dimmed">
              ·
            </Text>
            <Link
              to={projectLink}
              target="_blank"
              style={{ marginRight: '20px' }}
            >
              <Group gap={4}>
                <Text size="xs" c="blue" td="underline">
                  {notification.project.name}
                </Text>
                <IconExternalLink size={12} />
              </Group>
            </Link>
            <Badge
              variant="outline"
              size="xs"
              color="gray"
              leftSection={<IconBuildingStore size={12} />}
            >
              {notification.project.shop.name}
            </Badge>
            <Badge
              variant="outline"
              size="xs"
              color="gray"
              leftSection={<IconUserDollar size={12} />}
            >
              {notification.project.seller.firstName.charAt(0)}.{' '}
              {notification.project.seller.lastName}
            </Badge>
          </Group>
        </div>
      </Group>
      {isActive && onMarkAsSeen && (
        <ActionIcon
          variant="light"
          color="green"
          size="sm"
          title="Marquer comme vu"
          onClick={() => onMarkAsSeen(notification.id)}
        >
          <IconCheck size={14} />
        </ActionIcon>
      )}
      {isSeen && onMarkAsActive && (
        <ActionIcon
          variant="light"
          color="blue"
          size="sm"
          title="Remettre en cours"
          onClick={() => onMarkAsActive(notification.id)}
        >
          <IconArrowBackUp size={14} />
        </ActionIcon>
      )}
    </Group>
  );
};
